import format from 'date-fns/format';

/** BBA operates in Eastern Time; all clients should see the same session schedule. */
export const BREAKTHROUGH_TIMEZONE = 'America/New_York';

const easternFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: BREAKTHROUGH_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function parseFirestoreDate(value) {
  if (!value) return null;
  const date = value?.toDate && typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

function getEasternParts(date) {
  const parts = easternFormatter.formatToParts(date);
  return {
    year: Number(parts.find((p) => p.type === 'year').value),
    month: Number(parts.find((p) => p.type === 'month').value),
    day: Number(parts.find((p) => p.type === 'day').value),
    hours: Number(parts.find((p) => p.type === 'hour').value),
    minutes: Number(parts.find((p) => p.type === 'minute').value),
  };
}

/** Convert a calendar date/time in Eastern to a UTC Date instant. */
export function easternDateTimeToUtc(year, month, day, hours = 0, minutes = 0) {
  const targetMs = Date.UTC(year, month - 1, day, hours, minutes);
  let guess = targetMs + 5 * 60 * 60 * 1000;

  for (let attempt = 0; attempt < 5; attempt += 1) {
    const observed = getEasternParts(new Date(guess));
    const observedMs = Date.UTC(
      observed.year,
      observed.month - 1,
      observed.day,
      observed.hours,
      observed.minutes
    );
    const delta = targetMs - observedMs;
    if (delta === 0) return new Date(guess);
    guess += delta;
  }

  return new Date(guess);
}

export function getSessionCalendarParts(session) {
  const base = parseFirestoreDate(session?.date);
  if (!base) return null;
  const { year, month, day } = getEasternParts(base);
  return { year, month, day };
}

export function getBreakthroughTodayStart() {
  const { year, month, day } = getEasternParts(new Date());
  return easternDateTimeToUtc(year, month, day, 0, 0);
}

/** True when the session's Eastern calendar date falls in the current Eastern week (Mon–Sun). */
export function isSessionInCurrentWeek(session) {
  const calendar = getSessionCalendarParts(session);
  if (!calendar) return false;
  const today = getEasternParts(new Date());
  const todayUtc = Date.UTC(today.year, today.month - 1, today.day);
  const dayOfWeek = new Date(todayUtc).getUTCDay();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const weekStart = todayUtc + diffToMonday * 86400000;
  const weekEnd = weekStart + 7 * 86400000;
  const sessionUtc = Date.UTC(calendar.year, calendar.month - 1, calendar.day);
  return sessionUtc >= weekStart && sessionUtc < weekEnd;
}

export function parseSessionTimeString(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return { hours: 0, minutes: 0 };
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
  if (!match) return { hours: 0, minutes: 0 };

  let hours = Number(match[1]) % 24;
  const minutes = Number(match[2]) % 60;
  const ampm = match[3]?.toUpperCase();
  if (ampm) {
    hours = Number(match[1]) % 12;
    if (ampm === 'PM') hours += 12;
  }
  return { hours, minutes };
}

/** Session start instant in UTC, based on Eastern calendar date + startTime. */
export function sessionToStartDate(session) {
  const calendar = getSessionCalendarParts(session);
  if (!calendar) return null;
  const { hours, minutes } = parseSessionTimeString(session?.startTime || '0:00');
  return easternDateTimeToUtc(calendar.year, calendar.month, calendar.day, hours, minutes);
}

export function to12hr(timeStr) {
  if (!timeStr) return '';
  const upper = timeStr.toUpperCase();
  if (upper.includes('AM') || upper.includes('PM')) return timeStr;
  const [h, m] = timeStr.split(':').map(Number);
  if (isNaN(h)) return timeStr;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${period}`;
}

export function formatSessionDateLabel(session) {
  const calendar = getSessionCalendarParts(session);
  if (!calendar) return '';
  const date = new Date(calendar.year, calendar.month - 1, calendar.day);
  return format(date, 'EEEE, MMMM d');
}

export function formatSessionTimeLabel(session, { includeTimezone = true } = {}) {
  if (!session?.startTime) return '';
  const start = to12hr(session.startTime);
  const end = session.endTime ? ` – ${to12hr(session.endTime)}` : '';
  const suffix = includeTimezone ? ' ET' : '';
  return `${start}${end}${suffix}`;
}
